/**
 * <s-product-cart-summary> — "In your cart" list on the product page: every
 * colour (and bundle) of this product already in the shopper's cart.
 * Server-rendered from Liquid `cart`; re-rendered from the cart JSON whenever
 * the cart drawer opens (after each add) or closes (after edits in the drawer).
 *
 * Required child markup:
 *   [data-summary-list]  — <ul> the rows render into.
 *   [data-summary-count] — Item count text.
 *
 * Element dataset:
 *   data-product-id   — Only lines of this product are listed.
 *   data-cart-url     — routes.cart_url; ".js" is appended for the cart JSON.
 *   data-money-format — shop.money_format.
 *   data-count-one / data-count-label — "1 item" / "__count__ items".
 */
class SProductCartSummary extends HTMLElement {
  #ac;
  #fetchAc;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const refresh = () => this.#refresh();

    document.addEventListener('s-cart:opened', refresh, opts);
    const drawer = document.getElementById('cart-drawer');
    drawer?.addEventListener('close', refresh, opts);
    // A shopper returning with the back button may see a cached page; refresh it.
    window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchAc?.abort();
  }

  async #refresh() {
    this.#fetchAc?.abort();
    this.#fetchAc = new AbortController();
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#fetchAc.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(cart);
    } catch (error) {
      // Keep the last rendered list; the next drawer open retries.
    }
  }

  #render(cart) {
    const productId = String(this.dataset.productId);
    const items = (cart?.items || []).filter((item) => String(item.product_id) === productId);
    const list = this.querySelector('[data-summary-list]');
    const count = items.reduce((sum, item) => sum + item.quantity, 0);

    this.hidden = count === 0;
    const countEl = this.querySelector('[data-summary-count]');
    if (countEl) {
      countEl.textContent = count === 1
        ? (this.dataset.countOne || '1')
        : (this.dataset.countLabel || '__count__').replace('__count__', String(count));
    }
    if (!list) return;

    list.replaceChildren(...items.map((item) => this.#row(item)));
  }

  #row(item) {
    const li = document.createElement('li');
    li.className = 'flex items-start justify-between gap-3 py-2 text-sm';

    const info = document.createElement('span');
    info.className = 'min-w-0';

    const options = document.createElement('span');
    options.className = 'block text-on-surface';
    (item.options_with_values || []).forEach((option, index) => {
      if (index > 0) options.append(' · ');
      options.append(`${option.name}: `);
      const value = document.createElement('span');
      value.className = 'font-medium';
      value.textContent = option.value;
      options.append(value);
    });
    if (!item.options_with_values?.length) options.append(item.product_title || item.title || '');
    const qty = document.createElement('span');
    qty.className = 'text-on-surface-secondary';
    qty.textContent = ` × ${item.quantity}`;
    options.append(qty);
    info.append(options);

    for (const [key, value] of Object.entries(item.properties || {})) {
      if (!value || key.startsWith('_')) continue;
      const property = document.createElement('span');
      property.className = 'block text-xs text-on-surface-secondary';
      property.textContent = `${key}: ${value}`;
      info.append(property);
    }

    const price = document.createElement('bdi');
    price.className = 'shrink-0 font-medium text-on-surface';
    price.textContent = this.#money(item.final_line_price);

    li.append(info, price);
    return li;
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-product-cart-summary')) {
  customElements.define('s-product-cart-summary', SProductCartSummary);
}
